import React, { useState, useEffect, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { fetchPayouts } from "./graphHelper";
import { loginRequest } from "./authConfig";
import "./App.css";

const TABS = ["Dashboard", "Payouts", "By Person"];

function fmt(n) { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0); }
function fmtDate(d) { if (!d) return "—"; return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }

export default function App() {
  const { instance, accounts } = useMsal();
  const [payouts, setPayouts]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [tab, setTab]           = useState("Dashboard");
  const [search, setSearch]     = useState("");
  const [filterYear, setFilterYear] = useState("All");
  const [filterMonth, setFilterMonth] = useState("All");

  useEffect(() => { if (accounts.length > 0) load(); }, [accounts]);

  async function load() {
    setLoading(true); setError(null);
    try { const data = await fetchPayouts(instance, loginRequest); setPayouts(data); }
    catch (e) { setError("Failed to load: " + e.message); }
    setLoading(false);
  }

  const years  = useMemo(() => ["All", ...new Set(payouts.map(p => p.payoutDate ? new Date(p.payoutDate).getFullYear() : null).filter(Boolean).sort())], [payouts]);
  const MONTHS = ["All","January","February","March","April","May","June","July","August","September","October","November","December"];

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return payouts.filter(p => {
      const matchSearch = !q || (p.product||"").toLowerCase().includes(q);
      const matchYear   = filterYear  === "All" || (p.payoutDate && new Date(p.payoutDate).getFullYear() === parseInt(filterYear));
      const matchMonth  = filterMonth === "All" || (p.payoutDate && new Date(p.payoutDate).toLocaleString("en-US",{month:"long"}) === filterMonth);
      return matchSearch && matchYear && matchMonth;
    });
  }, [payouts, search, filterYear, filterMonth]);

  // Running totals
  const withRunning = useMemo(() => {
    let cumProfit=0, cumKaiser=0, cumSaim=0, cumFaisal=0, cumCompany=0;
    return filtered.map(p => {
      cumProfit  += p.profit;
      cumKaiser  += p.kaiser;
      cumSaim    += p.saim;
      cumFaisal  += p.faisal;
      cumCompany += p.company;
      return { ...p, cumProfit, cumKaiser, cumSaim, cumFaisal, cumCompany };
    });
  }, [filtered]);

  // Dashboard stats
  const totals = useMemo(() => ({
    profit:  filtered.reduce((s,p) => s+p.profit,  0),
    kaiser:  filtered.reduce((s,p) => s+p.kaiser,  0),
    saim:    filtered.reduce((s,p) => s+p.saim,    0),
    faisal:  filtered.reduce((s,p) => s+p.faisal,  0),
    company: filtered.reduce((s,p) => s+p.company, 0),
    count:   filtered.length,
  }), [filtered]);

  // Monthly breakdown
  const monthly = useMemo(() => {
    const months = {};
    filtered.forEach(p => {
      if (!p.payoutDate) return;
      const d   = new Date(p.payoutDate);
      const key = d.toLocaleString("en-US", { month: "long", year: "numeric" });
      const num = d.getFullYear() * 100 + d.getMonth();
      if (!months[key]) months[key] = { key, num, count:0, profit:0, kaiser:0, saim:0, faisal:0, company:0 };
      months[key].count++;
      months[key].profit  += p.profit;
      months[key].kaiser  += p.kaiser;
      months[key].saim    += p.saim;
      months[key].faisal  += p.faisal;
      months[key].company += p.company;
    });
    return Object.values(months).sort((a,b) => a.num - b.num);
  }, [filtered]);

  if (accounts.length === 0) {
    return (
      <div className="login-screen">
        <div className="login-card">
          <div className="logo">💵</div>
          <h1>Magoon Payouts</h1>
          <p>Track profit distributions for Kaiser, Saim, Faisal and Company</p>
          <button className="btn-primary" onClick={() => instance.loginPopup(loginRequest)}>Sign in with Microsoft 365</button>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="header">
        <div className="header-left"><span className="logo-sm">💵</span><span className="brand">Magoon Payouts</span></div>
        <nav className="tabs">{TABS.map(t => <button key={t} className={`tab ${tab===t?"active":""}`} onClick={()=>{setTab(t);setSearch("");}}>{t}</button>)}</nav>
        <div className="header-right">
          <span className="user">{accounts[0]?.name}</span>
          <button className="btn-ghost" onClick={() => instance.logoutPopup()}>Sign out</button>
        </div>
      </header>

      <main className="main">
        {loading && <div className="loading"><div className="spinner"></div><span>Loading payouts...</span></div>}
        {error && <div className="error">{error} <button onClick={load}>Retry</button></div>}

        {/* Filters — shown on all tabs */}
        {!loading && (
          <div className="filters-bar">
            {tab === "Payouts" && <input className="search-input" placeholder="Search by product..." value={search} onChange={e=>setSearch(e.target.value)} />}
            <select value={filterYear} onChange={e=>{setFilterYear(e.target.value);setFilterMonth("All");}}>
              {years.map(y=><option key={y} value={y}>{y==="All"?"Year":y}</option>)}
            </select>
            <select value={filterMonth} onChange={e=>setFilterMonth(e.target.value)}>
              {MONTHS.map(m=><option key={m} value={m}>{m==="All"?"Month":m}</option>)}
            </select>
            <button className="btn-ghost" onClick={()=>{setSearch("");setFilterYear("All");setFilterMonth("All");}}>Clear</button>
          </div>
        )}

        {/* DASHBOARD */}
        {tab === "Dashboard" && !loading && (
          <div>
            <h2 className="section-title">Payout Summary</h2>
            <div className="stat-grid">
              <div className="stat-card profit-card">
                <div className="stat-num">{fmt(totals.profit)}</div>
                <div className="stat-label">Total Profit</div>
              </div>
              <div className="stat-card kaiser-card">
                <div className="stat-num">{fmt(totals.kaiser)}</div>
                <div className="stat-label">Kaiser</div>
              </div>
              <div className="stat-card saim-card">
                <div className="stat-num">{fmt(totals.saim)}</div>
                <div className="stat-label">Saim</div>
              </div>
              <div className="stat-card faisal-card">
                <div className="stat-num">{fmt(totals.faisal)}</div>
                <div className="stat-label">Faisal</div>
              </div>
              <div className="stat-card company-card">
                <div className="stat-num">{fmt(totals.company)}</div>
                <div className="stat-label">Company</div>
              </div>
              <div className="stat-card">
                <div className="stat-num">{totals.count}</div>
                <div className="stat-label">Total Entries</div>
              </div>
            </div>

            {/* Distribution bar */}
            {totals.profit > 0 && (
              <div style={{marginTop:"2rem"}}>
                <h2 className="section-title">Profit Distribution</h2>
                <div className="dist-bar">
                  <div className="dist-seg kaiser-bg" style={{width:`${(totals.kaiser/totals.profit*100).toFixed(1)}%`}} title={`Kaiser: ${fmt(totals.kaiser)}`}><span>Kaiser</span></div>
                  <div className="dist-seg saim-bg"   style={{width:`${(totals.saim/totals.profit*100).toFixed(1)}%`}} title={`Saim: ${fmt(totals.saim)}`}><span>Saim</span></div>
                  <div className="dist-seg faisal-bg" style={{width:`${(totals.faisal/totals.profit*100).toFixed(1)}%`}} title={`Faisal: ${fmt(totals.faisal)}`}><span>Faisal</span></div>
                  <div className="dist-seg company-bg" style={{width:`${(totals.company/totals.profit*100).toFixed(1)}%`}} title={`Company: ${fmt(totals.company)}`}><span>Company</span></div>
                </div>
                <div className="dist-legend">
                  {[["Kaiser",totals.kaiser,"#3b82f6"],["Saim",totals.saim,"#22c55e"],["Faisal",totals.faisal,"#f59e0b"],["Company",totals.company,"#8b5cf6"]].map(([name,val,color])=>(
                    <div key={name} className="legend-item"><span className="legend-dot" style={{background:color}}></span><span>{name}: {fmt(val)} ({totals.profit>0?((val/totals.profit)*100).toFixed(1):0}%)</span></div>
                  ))}
                </div>
              </div>
            )}

            {/* Monthly breakdown */}
            <div style={{marginTop:"2rem"}}>
              <h2 className="section-title">Monthly Breakdown</h2>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Month</th><th>Entries</th><th>Profit</th><th>Kaiser</th><th>Saim</th><th>Faisal</th><th>Company</th></tr></thead>
                  <tbody>
                    {monthly.map(m => (
                      <tr key={m.key}>
                        <td><strong>{m.key}</strong></td>
                        <td>{m.count}</td>
                        <td className="profit-pos">{fmt(m.profit)}</td>
                        <td className="kaiser-col">{fmt(m.kaiser)}</td>
                        <td className="saim-col">{fmt(m.saim)}</td>
                        <td className="faisal-col">{fmt(m.faisal)}</td>
                        <td className="company-col">{fmt(m.company)}</td>
                      </tr>
                    ))}
                    {monthly.length === 0 && <tr><td colSpan={7} className="empty">No data.</td></tr>}
                    {monthly.length > 0 && (
                      <tr className="total-row">
                        <td><strong>TOTAL</strong></td>
                        <td><strong>{totals.count}</strong></td>
                        <td className="profit-pos"><strong>{fmt(totals.profit)}</strong></td>
                        <td className="kaiser-col"><strong>{fmt(totals.kaiser)}</strong></td>
                        <td className="saim-col"><strong>{fmt(totals.saim)}</strong></td>
                        <td className="faisal-col"><strong>{fmt(totals.faisal)}</strong></td>
                        <td className="company-col"><strong>{fmt(totals.company)}</strong></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* PAYOUTS TABLE */}
        {tab === "Payouts" && !loading && (
          <div>
            <div className="results-count">{filtered.length} entries</div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Date</th>
                    <th>Payout Date</th>
                    <th>Profit</th>
                    <th>Kaiser</th>
                    <th>Saim</th>
                    <th>Faisal</th>
                    <th>Company</th>
                    <th>Running Total</th>
                  </tr>
                </thead>
                <tbody>
                  {withRunning.map(p => (
                    <tr key={p.id}>
                      <td className="title-cell">{p.product}</td>
                      <td>{fmtDate(p.created)}</td>
                      <td>{fmtDate(p.payoutDate)}</td>
                      <td className="profit-pos">{fmt(p.profit)}</td>
                      <td className="kaiser-col">{fmt(p.kaiser)}</td>
                      <td className="saim-col">{fmt(p.saim)}</td>
                      <td className="faisal-col">{fmt(p.faisal)}</td>
                      <td className="company-col">{fmt(p.company)}</td>
                      <td><strong className="profit-pos">{fmt(p.cumProfit)}</strong></td>
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan={9} className="empty">No entries found.</td></tr>}
                  {filtered.length > 0 && (
                    <tr className="total-row">
                      <td colSpan={3}><strong>TOTAL</strong></td>
                      <td className="profit-pos"><strong>{fmt(totals.profit)}</strong></td>
                      <td className="kaiser-col"><strong>{fmt(totals.kaiser)}</strong></td>
                      <td className="saim-col"><strong>{fmt(totals.saim)}</strong></td>
                      <td className="faisal-col"><strong>{fmt(totals.faisal)}</strong></td>
                      <td className="company-col"><strong>{fmt(totals.company)}</strong></td>
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* BY PERSON */}
        {tab === "By Person" && !loading && (
          <div>
            {[
              { name: "Kaiser",  key: "kaiser",  color: "#3b82f6" },
              { name: "Saim",    key: "saim",    color: "#22c55e" },
              { name: "Faisal",  key: "faisal",  color: "#f59e0b" },
              { name: "Company", key: "company", color: "#8b5cf6" },
            ].map(person => {
              const personRows = filtered.filter(p => p[person.key] > 0);
              const personTotal = personRows.reduce((s,p) => s+p[person.key], 0);

              // Monthly breakdown for this person
              const personMonths = {};
              personRows.forEach(p => {
                if (!p.payoutDate) return;
                const d   = new Date(p.payoutDate);
                const key = d.toLocaleString("en-US", { month: "long", year: "numeric" });
                const num = d.getFullYear() * 100 + d.getMonth();
                if (!personMonths[key]) personMonths[key] = { key, num, count:0, profit:0, share:0 };
                personMonths[key].count++;
                personMonths[key].profit += p.profit;
                personMonths[key].share  += p[person.key];
              });
              const monthList = Object.values(personMonths).sort((a,b) => a.num - b.num);

              return (
                <div key={person.name} style={{marginBottom:"3rem"}}>
                  <h2 className="section-title" style={{color:person.color}}>
                    {person.name} — Total: {fmt(personTotal)}
                  </h2>

                  {/* Monthly summary */}
                  <h3 style={{color:"#94a3b8", fontSize:"0.85rem", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:"0.75rem"}}>Monthly Summary</h3>
                  <div className="table-wrap" style={{marginBottom:"1.5rem"}}>
                    <table>
                      <thead><tr><th>Month</th><th>Entries</th><th>Profit</th><th>{person.name} Share</th></tr></thead>
                      <tbody>
                        {monthList.map(m => (
                          <tr key={m.key}>
                            <td><strong>{m.key}</strong></td>
                            <td>{m.count}</td>
                            <td className="profit-pos">{fmt(m.profit)}</td>
                            <td style={{color:person.color, fontWeight:600}}>{fmt(m.share)}</td>
                          </tr>
                        ))}
                        {monthList.length > 0 && (
                          <tr className="total-row">
                            <td><strong>TOTAL</strong></td>
                            <td><strong>{personRows.length}</strong></td>
                            <td className="profit-pos"><strong>{fmt(personRows.reduce((s,p)=>s+p.profit,0))}</strong></td>
                            <td style={{color:person.color, fontWeight:700}}><strong>{fmt(personTotal)}</strong></td>
                          </tr>
                        )}
                        {monthList.length === 0 && <tr><td colSpan={4} className="empty">No entries.</td></tr>}
                      </tbody>
                    </table>
                  </div>

                  {/* Individual entries */}
                  <h3 style={{color:"#94a3b8", fontSize:"0.85rem", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:"0.75rem"}}>All Entries</h3>
                  <div className="table-wrap">
                    <table>
                      <thead><tr><th>Product</th><th>Date</th><th>Payout Date</th><th>Profit</th><th>{person.name} Share</th><th>Running Total</th></tr></thead>
                      <tbody>
                        {(() => {
                          let running = 0;
                          return personRows.map(p => {
                            running += p[person.key];
                            return (
                              <tr key={p.id}>
                                <td className="title-cell">{p.product}</td>
                                <td>{fmtDate(p.created)}</td>
                                <td>{fmtDate(p.payoutDate)}</td>
                                <td className="profit-pos">{fmt(p.profit)}</td>
                                <td style={{color:person.color, fontWeight:600}}>{fmt(p[person.key])}</td>
                                <td><strong style={{color:person.color}}>{fmt(running)}</strong></td>
                              </tr>
                            );
                          });
                        })()}
                        {personRows.length === 0 && <tr><td colSpan={6} className="empty">No entries.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
